import defaultsDeep from 'lodash/defaultsDeep'

import packageJson from 'package.json'

declare var __SETTINGS__: any // eslint-disable-line no-var
declare var __PUBLIC_PATH__: string // eslint-disable-line no-var

// All those settings MUST be defined for the app to "work",
// since we did not design a "reduced functionality" mode.
// In development mode thought the default values will be set to dummy strings.
interface IAppSettings {
  brand_id: string
  captcha_key: string
  client_id: string
  client_secret: string
  debounce_delay_on_filter_change: number
  display_mobile_footer: boolean
  display_penalty_scheme_tab?: boolean
  enable_agency_logo: boolean
  enable_heatmaps: boolean
  enable_mobile_version: boolean
  // Removes powerBI filters allowing data display on demo environment
  enable_powerbi_filters: boolean // DO NOT SET TO FALSE IN PRODUCTION
  enable_timeslots: boolean
  enable_trip_list: boolean
  enable_trip_user_kpis: boolean
  enable_vehicles_list: boolean
  filter_trips_max_distance_km: number
  filter_trips_max_duration: number
  filter_trips_max_speed_km_h: number
  filter_trips_min_distance_km: number
  filter_trips_min_duration: number
  filter_trips_min_speed_km_h: number
  filter_trips_step?: number
  fleet_enable_deployment_kpi: boolean
  fleet_enable_utilization_kpi: boolean
  fleet_enable_vehicle_list: boolean
  google_analytics_cookie_flags?: string
  google_analytics_id?: string
  hide_joyride: boolean
  language: string
  mapbox_token: string
  mobile_display_devices_zoom: number
  mobile_select_device_zoom: number
  mui_license_key: string
  otp_enabled: boolean
  password_changed_redirect_timeout: number
  password_login_enabled: boolean
  saml_enabled: boolean
  senmap_url?: string
  sentry_dsn?: string
  timezone: string
  trips_enable_environment_kpi: boolean
  trips_enable_heatmap: boolean
  trips_enable_trip_kpi: boolean
  trips_enable_trips_list: boolean
  trips_enable_users_kpi: boolean
  violation_range_range: number
  violation_range_units: string
}

interface ISettings {
  app: IAppSettings
  basePath: string
  debug: boolean
}

const defaultSettings: ISettings = {
  basePath: __PUBLIC_PATH__,
  debug: process.env.NODE_ENV !== 'production',
  app: {
    brand_id: '__DEFAULT__',
    captcha_key: '__DEFAULT__',
    client_id: '__DEFAULT__',
    client_secret: '__DEFAULT__',
    debounce_delay_on_filter_change: 500, // in ms
    display_mobile_footer: false,
    display_penalty_scheme_tab: false,
    enable_agency_logo: false,
    enable_mobile_version: false,
    enable_timeslots: false,
    mapbox_token: '__DEFAULT__',
    // Start displaying devices at the "suburb" level where 1px = 40m
    mobile_display_devices_zoom: 12,
    // use a close enough zoom level: the user is supposed to see the vehicles around him on the map.
    // 15 is around 5m per pixel, it's used to follow small roads usually.
    mobile_select_device_zoom: 15,
    mui_license_key: '__DEFAULT__',
    otp_enabled: false,
    filter_trips_step: undefined,
    filter_trips_max_distance_km: 15,
    filter_trips_max_duration: 1440,
    filter_trips_max_speed_km_h: 110,
    filter_trips_min_distance_km: 0,
    filter_trips_min_duration: 0,
    filter_trips_min_speed_km_h: 0,
    saml_enabled: false,
    hide_joyride: false,
    language: 'en',
    password_changed_redirect_timeout: 3000,
    password_login_enabled: true,
    timezone: 'Europe/Paris',
    violation_range_range: 3,
    violation_range_units: 'months',
    senmap_url: undefined,

    enable_vehicles_list: false,
    enable_trip_list: false,
    enable_trip_user_kpis: false,
    enable_heatmaps: false,
    fleet_enable_vehicle_list: true,
    fleet_enable_deployment_kpi: true,
    fleet_enable_utilization_kpi: true,
    trips_enable_trips_list: true,
    trips_enable_heatmap: true,
    trips_enable_trip_kpi: true,
    trips_enable_environment_kpi: true,
    trips_enable_users_kpi: true,
    // Removes powerBI filters allowing data display on demo environment
    enable_powerbi_filters: true, // DO NOT SET TO FALSE IN PRODUCTION
  },
}

/* the only purpose of this being an exported function is to be able to test the
 * settings override */
export function _getSettings(): ISettings {
  return defaultsDeep(
    {}, // object that gets recursively filled
    (window as any).__SETTINGS__, // settings set by app.settings.js
    (global as any).__SETTINGS__, // eventually overriden by settings from webpack & env
    __SETTINGS__, // eventually overriden by settings from .settings.yaml
    defaultSettings // and let the default hardcoded settings fill the gaps
  )
}

const SETTINGS = _getSettings()

// Make settings and build informations accessible for debug
;(global as any).PLATO_SETTINGS = SETTINGS
;(global as any).PLATO_BUILD = {
  tag: process.env.REACT_APP_BUILD_TAG,
  sha: process.env.REACT_APP_BUILD_SHA,
  version: packageJson.version,
}

export default SETTINGS
